import { useState } from "react";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Sector,
    Tooltip,
} from "recharts";

const hex2rgba = (hex: string, alpha = 1) => {
    // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
    const [r, g, b] = hex.match(/\w\w/g)!.map((x) => parseInt(x, 16));
    return `rgba(${r},${g},${b},${alpha})`;
};

const iR = 70;
const oR = 100;

export type SemiCircleChartDataItem<T> = {
    name: string;
    value: number;
    color: string;
} & T;

type SemiCircleChartProps<T> = {
    data: SemiCircleChartDataItem<T>[];
    onSectorClick?: (payload: SemiCircleChartDataItem<T>) => void;
};

type ActiveShapeProps<T> = {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    fill: string;
    payload: {
        payload: SemiCircleChartDataItem<T>;
    };
};

const renderActiveShape = <T,>(props: ActiveShapeProps<T> | undefined) => {
    if (props === undefined) return <></>;

    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
        props;

    return (
        <g>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                className="cursor-pointer"
            />
            <Sector
                cx={cx}
                cy={cy}
                startAngle={startAngle}
                endAngle={endAngle}
                innerRadius={outerRadius + 2}
                outerRadius={outerRadius + 12}
                fill={hex2rgba(fill, 0.3)}
            />
        </g>
    );
};

const SemiCircleChartKey = <T,>({
    data,
}: Pick<SemiCircleChartProps<T>, "data">) => {
    return (
        <div className="gap-1 pt-6 pl-3 place-content-center w-[230px]">
            {data.map((dataItem) => (
                <div className="flex items-center gap-2" key={dataItem.name}>
                    <div
                        className="w-[18px] h-2"
                        style={{ backgroundColor: dataItem.color }}
                    />
                    <div className="text-sm">
                        {dataItem.name} ({dataItem.value})
                    </div>
                </div>
            ))}
        </div>
    );
};

const SemiCircleChart = <T,>({
    data,
    onSectorClick,
}: SemiCircleChartProps<T>) => {
    const [activeIndex, setActiveIndex] = useState<number>();

    return (
        <>
            <div className="flex">
                <ResponsiveContainer width="50%" height={150}>
                    <PieChart>
                        <Pie
                            dataKey="value"
                            startAngle={180}
                            endAngle={0}
                            data={data}
                            cy={110}
                            innerRadius={iR}
                            outerRadius={oR}
                            fill="#8884d8"
                            stroke="none"
                            onMouseEnter={(_, i) => setActiveIndex(i)}
                            onMouseLeave={() => setActiveIndex(undefined)}
                            onMouseDown={(data: ActiveShapeProps<T>) =>
                                onSectorClick
                                    ? onSectorClick(data.payload.payload)
                                    : null
                            }
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                        >
                            {data.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                />
                            ))}
                        </Pie>
                        <Tooltip
                            content={(props) => {
                                const label = props.payload
                                    ? props.payload[0]?.name
                                    : "";
                                return (
                                    <div className="bg-surface-dark-5 text-white p-2 rounded-sm text-sm">
                                        View {label}
                                    </div>
                                );
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
                <SemiCircleChartKey data={data} />
            </div>
        </>
    );
};

export default SemiCircleChart;
